import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Plus, Tag, AlertCircle, CheckCircle } from 'lucide-react';
import { format, isToday, addHours, startOfDay } from 'date-fns';

interface Event {
  id: string;
  title: string;
  time: string;
  description?: string;
  priority: 'high' | 'medium' | 'low';
  completed?: boolean;
  tags?: string[];
}

interface DayViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date;
  events: Event[];
  onAddEvent: () => void;
  onToggleEventComplete: (eventId: string) => void;
}

const generateTimeSlots = (date: Date) => {
  const slots = [];
  const dayStart = startOfDay(date);
  
  for (let i = 0; i < 24; i++) {
    slots.push(addHours(dayStart, i));
  }
  
  return slots;
};

const DayViewModal: React.FC<DayViewModalProps> = ({
  isOpen,
  onClose,
  date,
  events,
  onAddEvent,
  onToggleEventComplete,
}) => {
  // Sort events by time
  const sortedEvents = [...events].sort((a, b) => {
    return new Date('1970/01/01 ' + a.time).getTime() - new Date('1970/01/01 ' + b.time).getTime();
  });

  // Group events by completion status
  const { completed, pending } = sortedEvents.reduce(
    (acc, event) => {
      if (event.completed) {
        acc.completed.push(event);
      } else {
        acc.pending.push(event);
      }
      return acc;
    },
    { completed: [] as Event[], pending: [] as Event[] }
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 day-view-backdrop z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-2xl w-full day-view-content">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {format(date, 'EEEE, MMMM d')}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                      {events.length} events scheduled
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Pending Events */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-yellow-500" />
                      Pending ({pending.length})
                    </h3>
                    <div className="space-y-3">
                      {pending.map((event) => (
                        <motion.div
                          key={event.id}
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          className={`p-4 bg-white dark:bg-dark-700 rounded-lg shadow-sm day-event
                            ${event.priority === 'high' ? 'priority-high' : ''}
                            ${event.priority === 'medium' ? 'priority-medium' : ''}
                            ${event.priority === 'low' ? 'priority-low' : ''}
                          `}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  {event.time}
                                </span>
                              </div>
                              <h4 className="text-lg font-medium text-gray-900 dark:text-white mt-1">
                                {event.title}
                              </h4>
                              {event.description && (
                                <p className="text-gray-600 dark:text-gray-300 mt-1">
                                  {event.description}
                                </p>
                              )}
                              {event.tags && event.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {event.tags.map((tag) => (
                                    <span
                                      key={tag}
                                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-dark-600 text-gray-600 dark:text-gray-300"
                                    >
                                      <Tag className="h-3 w-3 mr-1" />
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => onToggleEventComplete(event.id)}
                              className="p-2 text-gray-400 hover:text-green-500 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                            >
                              <CheckCircle className="h-5 w-5" />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Completed Events */}
                  {completed.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                        <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
                        Completed ({completed.length})
                      </h3>
                      <div className="space-y-3">
                        {completed.map((event) => (
                          <motion.div
                            key={event.id}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            className="p-4 bg-gray-50 dark:bg-dark-700/50 rounded-lg day-event"
                          >
                            <div className="flex items-start justify-between opacity-50">
                              <div className="flex-1">
                                <div className="flex items-center">
                                  <Clock className="h-4 w-4 text-gray-400 mr-2" />
                                  <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
                                    {event.time}
                                  </span>
                                </div>
                                <h4 className="text-lg font-medium text-gray-900 dark:text-white mt-1 line-through">
                                  {event.title}
                                </h4>
                              </div>
                              <button
                                onClick={() => onToggleEventComplete(event.id)}
                                className="p-2 text-green-500 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                              >
                                <CheckCircle className="h-5 w-5" />
                              </button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Event Button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onAddEvent}
                    className="w-full mt-6 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add New Event
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DayViewModal;